import React, { useState, useEffect, useCallback } from 'react';
import { FiSearch, FiX, FiPlus, FiSave, FiUser, FiKey } from 'react-icons/fi';
import { listarAcessosClientes, criarAcessoCliente, atualizarAcessoCliente } from '../../services/portalClientes';
import { formatarDocumento } from '../../utils/documento';
import useEsc from '../../hooks/useEsc';

/* Configurações › Acesso de clientes.
   Licença do módulo NF: o cliente entra no mesmo endereço do app, mas só
   enxerga o menu NF -- e dentro dele só os módulos marcados aqui. Os
   parâmetros continuam com o escritório; o cliente cadastra fornecedores
   e emite. */

const caixa = { background: '#fff', border: '1px solid #e4edf5', borderRadius: 12, padding: 16 };
const entrada = { width: '100%', padding: '8px 10px', border: '1px solid #d6e2ee', borderRadius: 8, fontSize: 13, boxSizing: 'border-box' };
const rotulo = { fontSize: 11, fontWeight: 700, color: '#6B8CAE', textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', marginBottom: 4 };
const btn = (pri) => ({
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer',
  border: pri ? 'none' : '1px solid #d6e2ee', background: pri ? '#1e3a5f' : '#fff', color: pri ? '#fff' : '#1e3a5f',
});

function ModalCliente({ cliente, modulos, onFechar, onMudou }) {
  useEsc(onFechar);
  const vazio = { id: null, email: '', nome: '', senha: '', menus: ['nf'], ativo: true };
  const [form, setForm] = useState(null); // null = lista; objeto = criando/editando
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const set = (c, v) => setForm(f => ({ ...f, [c]: v }));
  const alternarModulo = (k) => setForm(f => ({ ...f, menus: f.menus.includes(k) ? f.menus.filter(m => m !== k) : [...f.menus, k] }));

  async function gravar() {
    setErro(''); setSalvando(true);
    try {
      if (form.id) {
        await atualizarAcessoCliente(form.id, { nome: form.nome, menus: form.menus, ativo: form.ativo, ...(form.senha ? { senha: form.senha } : {}) });
      } else {
        await criarAcessoCliente({ clienteId: cliente.id, email: form.email, senha: form.senha, nome: form.nome, menus: form.menus });
      }
      setForm(null);
      await onMudou();
    } catch (e) { setErro(e.message); }
    finally { setSalvando(false); }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(13,31,60,.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ ...caixa, width: '100%', maxWidth: 620, maxHeight: '90vh', overflowY: 'auto', padding: 20 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 14 }}>
          <div style={{ flex: 1 }}>
            <h3 style={{ margin: 0, fontSize: 16, color: '#15365c' }}>{cliente.codigo} - {cliente.nome}</h3>
            <p style={{ margin: '2px 0 0', fontSize: 12, color: '#6B8CAE' }}>{formatarDocumento(cliente.cnpj || cliente.cpf)}</p>
          </div>
          <button type="button" style={btn(false)} onClick={onFechar} title="Fechar (Esc)"><FiX size={14} /></button>
        </div>

        {erro && <p style={{ color: '#b91c1c', fontSize: 13, margin: '0 0 10px' }}>{erro}</p>}

        {!form ? (
          <>
            {cliente.usuarios.length === 0 && (
              <p style={{ fontSize: 13, color: '#6B8CAE' }}>Nenhum acesso para este cliente ainda.</p>
            )}
            {cliente.usuarios.map(u => (
              <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', border: '1px solid #e4edf5', borderRadius: 10, marginBottom: 8, opacity: u.ativo ? 1 : 0.55 }}>
                <FiUser size={15} color="#4A90D9" />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 13 }}>{u.nome || u.email} {!u.ativo && <span style={{ color: '#b91c1c', fontWeight: 600 }}>· desativado</span>}</div>
                  <div style={{ fontSize: 12, color: '#6B8CAE' }}>{u.email} · {u.menus.map(m => modulos.find(x => x.key === m)?.label.split(' (')[0] || m).join(', ') || 'nenhum módulo'}</div>
                </div>
                <button type="button" style={btn(false)} onClick={() => { setErro(''); setForm({ ...vazio, ...u, senha: '' }); }}>Editar</button>
              </div>
            ))}
            <button type="button" style={{ ...btn(true), marginTop: 6 }} onClick={() => { setErro(''); setForm(vazio); }}>
              <FiPlus size={14} /> Novo acesso
            </button>
          </>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <span style={rotulo}>E-mail (login)</span>
                <input style={{ ...entrada, background: form.id ? '#f4f7fb' : '#fff' }} value={form.email} readOnly={!!form.id}
                  onChange={e => set('email', e.target.value)} placeholder="financeiro@cliente.com.br" />
              </div>
              <div>
                <span style={rotulo}>Nome</span>
                <input style={entrada} value={form.nome} onChange={e => set('nome', e.target.value)} placeholder="Quem vai usar" />
              </div>
              <div>
                <span style={rotulo}><FiKey size={10} /> {form.id ? 'Nova senha (vazio = manter)' : 'Senha (mín. 8)'}</span>
                <input style={entrada} type="password" autoComplete="new-password" value={form.senha} onChange={e => set('senha', e.target.value)} />
              </div>
              {form.id && (
                <div>
                  <span style={rotulo}>Situação</span>
                  <select style={entrada} value={form.ativo ? '1' : '0'} onChange={e => set('ativo', e.target.value === '1')}>
                    <option value="1">Ativo</option>
                    <option value="0">Desativado (não entra mais)</option>
                  </select>
                </div>
              )}
            </div>

            <span style={{ ...rotulo, marginTop: 16 }}>Módulos liberados</span>
            {modulos.map(m => (
              <label key={m.key} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, padding: '5px 0', cursor: 'pointer' }}>
                <input type="checkbox" checked={form.menus.includes(m.key)} onChange={() => alternarModulo(m.key)} />
                {m.label}
              </label>
            ))}
            <p style={{ fontSize: 12, color: '#6B8CAE', margin: '8px 0 0' }}>
              Os parâmetros de NF, perfis e certificado continuam com o escritório. O cliente só cadastra fornecedores e emite.
            </p>

            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button type="button" style={btn(false)} onClick={() => setForm(null)}>Voltar</button>
              <button type="button" style={btn(true)} onClick={gravar} disabled={salvando}>
                <FiSave size={14} /> {salvando ? 'Gravando...' : 'Gravar'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function AcessoClientesTab() {
  const [dados, setDados] = useState({ modulos: [], clientes: [] });
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [busca, setBusca] = useState('');
  const [aberto, setAberto] = useState(null); // id do cliente

  const carregar = useCallback(() => listarAcessosClientes()
    .then(setDados).catch(e => setErro(e.message)).finally(() => setCarregando(false)), []);
  useEffect(() => { carregar(); }, [carregar]);

  const q = busca.trim().toLowerCase();
  const digitos = busca.replace(/\D/g, '');
  const lista = dados.clientes
    .filter(c => !q || `${c.codigo} ${c.nome}`.toLowerCase().includes(q) || (digitos && String(c.cnpj || c.cpf || '').replace(/\D/g, '').includes(digitos)))
    /* quem já tem acesso primeiro */
    .sort((a, b) => (b.usuarios.length > 0) - (a.usuarios.length > 0));
  const clienteAberto = dados.clientes.find(c => c.id === aberto);

  return (
    <div>
      <p style={{ fontSize: 13, color: '#4A6B8A', marginTop: 0 }}>
        Clientes com licença do módulo NF. O cliente entra pelo mesmo endereço do sistema, com o e-mail e a senha cadastrados aqui,
        e vê só o menu NF com os módulos liberados.
      </p>
      <div style={{ position: 'relative', maxWidth: 360, marginBottom: 12 }}>
        <FiSearch size={13} style={{ position: 'absolute', left: 10, top: 11, color: '#9ab0c4' }} />
        <input style={{ ...entrada, paddingLeft: 30 }} placeholder="Código, nome ou CNPJ..." value={busca} onChange={e => setBusca(e.target.value)} />
      </div>
      {erro && <p style={{ color: '#b91c1c', fontSize: 13 }}>{erro}</p>}
      {carregando ? <p style={{ fontSize: 13, color: '#6B8CAE' }}>Carregando...</p> : (
        <div style={{ display: 'grid', gap: 6 }}>
          {lista.map(c => (
            <button key={c.id} type="button" onClick={() => setAberto(c.id)}
              style={{ display: 'flex', alignItems: 'center', gap: 12, textAlign: 'left', padding: '10px 14px', border: '1px solid #e4edf5', borderRadius: 10, background: '#fff', cursor: 'pointer', font: 'inherit' }}>
              <strong style={{ color: '#6B8CAE', minWidth: 44 }}>{c.codigo}</strong>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontWeight: 700, fontSize: 13, color: '#15365c' }}>{c.nome}</span>
                <span style={{ fontSize: 12, color: '#6B8CAE' }}>{formatarDocumento(c.cnpj || c.cpf)}</span>
              </span>
              <span style={{ fontSize: 12, fontWeight: 700, color: c.usuarios.some(u => u.ativo) ? '#047857' : '#9ab0c4' }}>
                {c.usuarios.filter(u => u.ativo).length ? `${c.usuarios.filter(u => u.ativo).length} acesso(s)` : 'sem acesso'}
              </span>
            </button>
          ))}
        </div>
      )}
      {clienteAberto && (
        <ModalCliente cliente={clienteAberto} modulos={dados.modulos} onFechar={() => setAberto(null)} onMudou={carregar} />
      )}
    </div>
  );
}

export default AcessoClientesTab;
